import React, { useEffect, useRef, useState } from "react";
import SVGA from "svgaplayerweb";
import "./MaleLevelFrame.css";

import u1 from "../../assets/R1.svga";
import u2 from "../../assets/R2.svga";
import u3 from "../../assets/R3.svga";
import u4 from "../../assets/R4.svga";
import u5 from "../../assets/R5.svga";
import u6 from "../../assets/R6.svga";
import u7 from "../../assets/R7.svga";
import u8 from "../../assets/R8.svga";
import u9 from "../../assets/R9.svga";
import u10 from "../../assets/R10.svga";

export const MALE_RANK_SVGA_URLS = [
  null,
  u1,
  u2,
  u3,
  u4,
  u5,
  u6,
  u7,
  u8,
  u9,
  u10,
];

/**
 * LOCKED — MaleLevel hero SVGA frame.
 * لا تغيّر هذه القيم إلا إذا أردت تعديل الإطار العلوي فقط.
 */
export const RANK_FRAME_CALIBRATION = {
  1: { slot: 1.25, scale: 0.95, avatarInset: "18.5%", x: 0, y: 0 },
  2: { slot: 1.3, scale: 0.96, avatarInset: "19%", x: 0, y: 0 },
  3: { slot: 1.3, scale: 1.04, avatarInset: "18.5%", x: 0, y: 2 },
  4: { slot: 1.18, scale: 0.88, avatarInset: "18.5%", x: 0, y: 0 },
  5: { slot: 1.3, scale: 0.96, avatarInset: "19%", x: 0, y: 0 },
  6: { slot: 1.3, scale: 1.04, avatarInset: "18.5%", x: 0, y: 0 },
  7:  { slot: 1.3, scale: 0.96, avatarInset: "19%", x: 0, y: 0 },
  8: { slot: 1.3, scale: 0.96, avatarInset: "19%", x: 0, y: 0 },
  9: { slot: 1.3, scale: 1.04, avatarInset: "18.5%", x: 0, y: 0 },
  10: { slot: 1.18, scale: 1.02, avatarInset: "18.5%", x: 0, y: 0 },
};

function destroyPlayer(player) {
  try {
    if (!player) return;
    player.stopAnimation?.();
    player.clear?.();
    player.destroy?.();
  } catch (_) {}
}

/**
 * خاص بالكروت السفلية فقط.
 * لا يجبر canvas على حجم معين.
 * ينتظر حتى تصبح الحاوية لها عرض/ارتفاع قبل تشغيل SVGA.
 */
export function MaleRankSvgaHost({
  src,
  className = "",
  fallbackClassName = "",
}) {
  const hostRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!src) {
      setFailed(true);
      return;
    }

    let mounted = true;
    let player = null;
    let parser = null;
    let rafId = null;
    let retryCount = 0;

    const cleanup = () => {
      try {
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }

        if (player) {
          player.stopAnimation?.();
          player.clear?.();
          player.destroy?.();
        }
      } catch (_) {}
    };

    const initPlayer = () => {
      if (!mounted) return;

      const el = hostRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();

      if ((!rect.width || !rect.height) && retryCount < 30) {
        retryCount += 1;
        rafId = requestAnimationFrame(initPlayer);
        return;
      }

      try {
        player = new SVGA.Player(el);
        parser = new SVGA.Parser();

        player.loops = 0;
        player.clearsAfterStop = false;
        player.fillMode = "Forward";

        parser.load(
          src,
          (videoItem) => {
            if (!mounted || !videoItem || !player) return;

            player.setVideoItem(videoItem);
            player.setContentMode("AspectFit");
            player.startAnimation();
          },
          () => {
            if (!mounted) return;
            cleanup();
            player = null;
            setFailed(true);
          }
        );
      } catch {
        if (mounted) setFailed(true);
      }
    };

    rafId = requestAnimationFrame(initPlayer);

    return () => {
      mounted = false;
      cleanup();
    };
  }, [src]);

  if (!src || failed) {
    return (
      <div
        className={`mlf-svga-fallback-ring mlf-svga-fallback-ring--small ${fallbackClassName}`}
        aria-hidden
      />
    );
  }

  return (
    <div
      ref={hostRef}
      className={`mlf-svga-host-inner ${className}`}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflow: "visible",
        pointerEvents: "none",
      }}
      aria-hidden
    />
  );
}

export default function MaleLevelFrame({
  displayLevel = 1,
  imageUrl = "",
  size = "clamp(170px, 46vw, 230px)",
  showBadge = true,
  className = "",
}) {
  const dl = Math.max(1, Math.min(10, Number(displayLevel) || 1));
  const frameUrl = MALE_RANK_SVGA_URLS[dl];

  const hostRef = useRef(null);
  const [svgaFailed, setSvgaFailed] = useState(false);

  const frameSizeCss =
    typeof size === "number"
      ? `${size}px`
      : String(size || "clamp(170px, 46vw, 230px)");

  const frameCalib =
    RANK_FRAME_CALIBRATION[dl] || RANK_FRAME_CALIBRATION[1];

  useEffect(() => {
    if (!frameUrl || !hostRef.current) return;

    let mounted = true;
    let player = null;
    let parser = null;

    try {
      player = new SVGA.Player(hostRef.current);
      parser = new SVGA.Parser();

      player.loops = 0;
      player.clearsAfterStop = false;
      player.fillMode = "Forward";

      parser.load(
        frameUrl,
        (videoItem) => {
          if (!mounted || !videoItem || !player) return;

          player.setVideoItem(videoItem);
          player.setContentMode("AspectFit");
          player.startAnimation();
        },
        () => {
          if (!mounted) return;

          destroyPlayer(player);
          player = null;
          setSvgaFailed(true);
        }
      );
    } catch {
      if (mounted) setSvgaFailed(true);
    }

    return () => {
      mounted = false;
      destroyPlayer(player);
    };
  }, [frameUrl]);

  return (
    <div
      className={`male-level-frame male-level-frame--rank-${dl} ${className}`}
      style={{
        "--frame-size": frameSizeCss,
        "--mlf-svga-slot-mult": String(frameCalib.slot),
        "--mlf-avatar-inset": frameCalib.avatarInset,
        "--rank-frame-scale": String(frameCalib.scale),
        "--rank-frame-x": `${frameCalib.x}%`,
        "--rank-frame-y": `${frameCalib.y}%`,
      }}
      data-male-rank={dl}
    >
      <div className="mlf-aura" aria-hidden />
      <div className="mlf-orbit mlf-orbit-a" aria-hidden />
      <div className="mlf-orbit mlf-orbit-b" aria-hidden />

      {frameUrl && !svgaFailed ? (
        <div ref={hostRef} className="mlf-svga-layer" aria-hidden />
      ) : (
        <div
          className="mlf-svga-fallback-ring mlf-svga-fallback-ring--hero"
          aria-hidden
        />
      )}

      <div className="mlf-core">
        <div className="mlf-photo-shell">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="mlf-avatar" />
          ) : (
            <div className="mlf-avatar mlf-avatar-placeholder" aria-hidden />
          )}
        </div>
      </div>

      {showBadge && (
        <div className="mlf-badge">
          <span>Lv{dl}</span>
        </div>
      )}
    </div>
  );
}